import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useImpersonationPath } from '@/hooks/useImpersonationPath'
import { User, UserRole } from '@/types'
import { ROLE_LABELS } from '@/utils/role-access'
import { CaretDown, SignOut, User as UserIcon } from 'phosphor-react'

interface UserMenuProps {
  user: User
}

export const UserMenu: React.FC<UserMenuProps> = ({ user }) => {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const toPath = useImpersonationPath()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const getInitials = () => {
    return `${user.firstname.charAt(0)}${user.lastname.charAt(0)}`.toUpperCase()
  }

  const getRoleLabel = (role: UserRole) => ROLE_LABELS[role] ?? role

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--color-gray-100)] transition-colors"
      >
        <div className="flex items-center justify-center w-10 h-10 bg-[var(--color-primary)] text-white rounded-full font-semibold">
          {getInitials()}
        </div>
        <div className="text-left hidden sm:block">
          <div className="text-sm font-medium text-[var(--color-text-primary)]">
            {user.firstname} {user.lastname}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">{getRoleLabel(user.role)}</div>
        </div>
        <CaretDown
          className={`w-5 h-5 text-[var(--color-text-muted)] transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          weight="regular"
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-[var(--color-border)] z-50 overflow-hidden">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-12 h-12 bg-[var(--color-primary)] text-white rounded-full font-semibold">
                {getInitials()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[var(--color-text-primary)] truncate">
                  {user.firstname} {user.lastname}
                </div>
                <div className="text-xs text-[var(--color-text-muted)] truncate">{user.email}</div>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-[var(--color-text-muted)]">Rôle:</span>
                <span className="font-medium text-[var(--color-text-primary)]">
                  {getRoleLabel(user.role)}
                </span>
              </div>
              {user.phone && (
                <div className="flex items-center justify-between">
                  <span className="text-[var(--color-text-muted)]">Téléphone:</span>
                  <span className="font-medium text-[var(--color-text-primary)]">{user.phone}</span>
                </div>
              )}
              {user.lastLogin && (
                <div className="flex items-center justify-between">
                  <span className="text-[var(--color-text-muted)]">Dernière connexion:</span>
                  <span className="font-medium text-[var(--color-text-primary)] text-xs">
                    {new Date(user.lastLogin).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div>
            <button
              onClick={() => {
                navigate(toPath('/profile'))
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 p-4 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-gray-100)] transition-colors border-b border-[var(--color-border)]"
            >
              <UserIcon className="w-5 h-5" weight="regular" />
              <span>Mon profil</span>
            </button>
            <button
              onClick={() => {
                logout()
                setIsOpen(false)
                navigate('/login')
              }}
              className="w-full flex items-center gap-3 p-4 text-sm text-[var(--color-error)] hover:bg-red-50 transition-colors"
            >
              <SignOut className="w-5 h-5" weight="regular" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
